import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Card,
  CardHeader,
  CardBody,
  Heading,
  useColorModeValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
} from '@chakra-ui/react';
import { FiSearch, FiDownload, FiFilter, FiTrendingUp, FiTrendingDown, FiUsers, FiClock, FiPhone } from 'react-icons/fi';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const Analytics = () => {
  const [dateRange, setDateRange] = useState('7d');
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Dummy data for call volume over time
  const callVolumeData = [
    { date: '2024-03-10', calls: 120, completed: 100, failed: 20 },
    { date: '2024-03-11', calls: 150, completed: 130, failed: 20 },
    { date: '2024-03-12', calls: 180, completed: 160, failed: 20 },
    { date: '2024-03-13', calls: 200, completed: 180, failed: 20 },
    { date: '2024-03-14', calls: 220, completed: 200, failed: 20 },
    { date: '2024-03-15', calls: 250, completed: 230, failed: 20 },
    { date: '2024-03-16', calls: 280, completed: 260, failed: 20 },
  ];

  // Dummy data for call distribution
  const callDistributionData = [
    { name: 'Sales', value: 40 },
    { name: 'Support', value: 30 },
    { name: 'Survey', value: 20 },
    { name: 'Other', value: 10 },
  ];

  // Dummy data for call duration
  const callDurationData = [
    { date: '2024-03-10', avgDuration: 4.5 },
    { date: '2024-03-11', avgDuration: 5.2 },
    { date: '2024-03-12', avgDuration: 4.8 },
    { date: '2024-03-13', avgDuration: 5.5 },
    { date: '2024-03-14', avgDuration: 4.9 },
    { date: '2024-03-15', avgDuration: 5.1 },
    { date: '2024-03-16', avgDuration: 5.3 },
  ];

  // Calculate statistics
  const totalCalls = callVolumeData.reduce((sum, day) => sum + day.calls, 0);
  const completedCalls = callVolumeData.reduce((sum, day) => sum + day.completed, 0);
  const averageDuration = callDurationData.reduce((sum, day) => sum + day.avgDuration, 0) / callDurationData.length;
  const successRate = (completedCalls / totalCalls) * 100;

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Heading size="lg">Analytics</Heading>
          <HStack>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              width="150px"
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="1y">Last Year</option>
            </Select>
            <Button
              leftIcon={<FiDownload />}
              variant="outline"
              onClick={() => {
                toast({
                  title: 'Export Started',
                  description: 'Exporting analytics data...',
                  status: 'info',
                  duration: 3000,
                });
              }}
            >
              Export
            </Button>
          </HStack>
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
          <Card bg={bgColor} boxShadow="md" borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <StatLabel>Total Calls</StatLabel>
                <StatNumber>{totalCalls}</StatNumber>
                <StatHelpText>
                  <HStack>
                    <FiTrendingUp color="green" />
                    <Text>+12% from last period</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={bgColor} boxShadow="md" borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <StatLabel>Success Rate</StatLabel>
                <StatNumber>{successRate.toFixed(1)}%</StatNumber>
                <StatHelpText>
                  <HStack>
                    <FiTrendingUp color="green" />
                    <Text>+2% from last period</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={bgColor} boxShadow="md" borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <StatLabel>Average Duration</StatLabel>
                <StatNumber>{averageDuration.toFixed(1)} min</StatNumber>
                <StatHelpText>
                  <HStack>
                    <FiTrendingDown color="red" />
                    <Text>-0.5 min from last period</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={bgColor} boxShadow="md" borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <StatLabel>Active Agents</StatLabel>
                <StatNumber>12</StatNumber>
                <StatHelpText>
                  <HStack>
                    <FiUsers />
                    <Text>Across all campaigns</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Card bg={bgColor} boxShadow="md" borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Heading size="md">Call Volume</Heading>
            </CardHeader>
            <CardBody>
              <Box height="300px">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={callVolumeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="calls" stroke="#8884d8" name="Total Calls" />
                    <Line type="monotone" dataKey="completed" stroke="#82ca9d" name="Completed" />
                    <Line type="monotone" dataKey="failed" stroke="#ff7300" name="Failed" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardBody>
          </Card>

          <Card bg={bgColor} boxShadow="md" borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Heading size="md">Call Distribution</Heading>
            </CardHeader>
            <CardBody>
              <Box height="300px">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={callDistributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {callDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardBody>
          </Card>

          <Card bg={bgColor} boxShadow="md" borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Heading size="md">Average Call Duration</Heading>
            </CardHeader>
            <CardBody>
              <Box height="300px">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={callDurationData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="avgDuration" stroke="#8884d8" name="Duration (min)" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardBody>
          </Card>

          <Card bg={bgColor} boxShadow="md" borderColor={borderColor} borderWidth="1px">
            <CardHeader>
              <Heading size="md">Call Performance</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text>Peak Hours</Text>
                  <Text fontWeight="bold">2 PM - 4 PM</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text>Best Day</Text>
                  <Text fontWeight="bold">Wednesday</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text>Most Active Agent</Text>
                  <Text fontWeight="bold">John Doe (150 calls)</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text>Highest Success Rate</Text>
                  <Text fontWeight="bold">Sales Campaign (92%)</Text>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>
      </VStack>
    </Box>
  );
};

export default Analytics;
